import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';

import useTimeOfUsable from 'src/util/i18n/timeOfUse/useTimeOfUsable';

/**
 * Renders a simple time-of-usable entity as a single inline span with price information
 * and an "At all times" message.
 *
 * **Use this component when:** You need to display simple time-of-use information inline
 * within text, tables, or compact layouts where horizontal space is limited and all
 * information should appear on a single line.
 *
 * **Key differences from Simple:**
 * - Renders all content inline within a single `<span>` element separated by spaces
 * - All information (price and "At all times" message) appears on the same line
 * - Suitable for tables, inline text, or compact layouts where vertical space is constrained
 * - Returns a single span element containing space-separated content
 *
 * **When to use Simple vs SimpleInline:**
 * - Use `Simple` for structured layouts, forms, or detailed views with vertical space
 * - Use `SimpleInline` for tables, inline text, or compact horizontal layouts
 * @param {object} props - Component props
 * @param {object} props.timeOfUsable - The time-of-usable entity containing pricing
 * and identification information
 * @param {string} [props.valueField='price'] - The value to be converted and formatted as a
 * price or rate.
 * @returns {React.ReactElement} A span element containing the formatted price and
 *   "At all times" message separated by a space
 */
function SimpleInline({ timeOfUsable, valueField = 'price' }) {
  const intl = useIntl();

  if (!timeOfUsable || Object.keys(timeOfUsable).length === 0) {
    return null;
  }

  const { uuid } = timeOfUsable;
  const { value } = useTimeOfUsable(timeOfUsable, valueField);

  const atAllTimesMsg = intl.formatMessage({
    id: 'trade_rule.time_of_use.time_of_usable.simple_inline.at_all_times.text',
    defaultMessage: 'At all times',
  });

  return (
    <span key={uuid}>
      {value}
      {' '}
      {atAllTimesMsg}
    </span>
  );
}

SimpleInline.propTypes = {
  timeOfUsable: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  valueField: PropTypes.string,
};

SimpleInline.defaultProps = {
  valueField: 'price',
};

export default SimpleInline;
